import React, { useState, useEffect, useCallback, useContext } from 'react';
import GameAssetsContext from '../../XXX/context/GameAssetContext';
import { championImages } from '../../XXX/GameSimulator/GameContainer2/GameGrid/ChampionCard/ComboDisplay/Events/Images/championImages';
import PracticeArea from './PracticeArea';
import KitingSettings from './KitingSettings';

import ComboSelector from './ComboSelector';
import SequenceDebugger from './SequenceDebugger';
import useSequenceLogic from './useSequenceLogic';
import './ComboInputPractice.css';
import './SequenceDebugger.css';

interface DetailedKey {
  key: string;
  imageState?: string;
  target?: string;
  description: string;
}

interface ComboData {
  id: string;
  name: string;
  description: string;
  championName: string;
  difficulty: string;
  category: string;
  type: 'simple-combo' | 'jungle-clear';
  inputSequence: string;
  detailedSequence: DetailedKey[];
}

interface ComboDatabase {
  combos: ComboData[];
  categories: Array<{ id: string; name: string; description: string }>;
  difficulties: Array<{ id: string; name: string; color: string }>;
}

interface ParsedKey {
  baseKey: string;
  imageState?: string;
  displayKey: string;
  keyType: 'spell' | 'summoner' | 'auto' | 'item' | 'rightclick' | 'leftclick';
  target?: string;
  description?: string;
  requiresConfirmation?: boolean;
  subSequenceId?: number;
  subSequencePosition?: number;
}

interface SummonerSpellSettings {
  smite: string; // default 'D'
  flash: string; // default 'F'
}

interface KitingSettings {
  enabled: boolean;
  rightClickKey: string; // default 'Right Click'
  hideRCButtons: boolean; // hide RC buttons in display
  hideLCButtons: boolean; // hide LC buttons in display
  attackMoveClick: boolean; // require left-click confirmation for auto attacks
}

interface AlignedInput {
  expected: ParsedKey;
  subSequenceKeys?: ParsedKey[]; // For A+LC groups
  userInputs: Array<{key: string, isCorrect: boolean, isOptional?: boolean, position?: number}>;
  followingRCs: Array<{key: string, isCorrect: boolean, isOptional?: boolean}>;
  isMainKey: boolean;
  isSubSequenceGroup?: boolean; // True for A+LC groups
}

interface HighScore {
  comboName: string;
  time: number;
  accuracy: number;
  kpm: number;
  errors: number;
  date: string;
}

// This will be replaced by loaded data
let COMBO_DATABASE: ComboDatabase = {
  combos: [],
  categories: [],
  difficulties: []
};

const ComboInputPractice: React.FC = () => {
  const [combos, setCombos] = useState<ComboData[]>([]);
  const [selectedCombo, setSelectedCombo] = useState<ComboData | null>(null);
  const [customSequence, setCustomSequence] = useState<string>('');
  const [currentKeyIndex, setCurrentKeyIndex] = useState<number>(0);
  const [practiceMode, setPracticeMode] = useState<boolean>(false);
  const [userInputKeys, setUserInputKeys] = useState<Array<{key: string, isCorrect: boolean, isOptional?: boolean}>>([]);
  const [errors, setErrors] = useState<number>(0);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [endTime, setEndTime] = useState<number | null>(null);
  const [summonerSettings, setSummonerSettings] = useState<SummonerSpellSettings>({
    smite: 'D',
    flash: 'F'
  });
  const [kitingSettings, setKitingSettings] = useState<KitingSettings>({
    enabled: true, // default enabled as requested
    rightClickKey: 'Right Click',
    hideRCButtons: true, // default off for visual
    hideLCButtons: true, // default on - hide LC buttons
    attackMoveClick: false // default off
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [showCompletion, setShowCompletion] = useState<boolean>(false);
  const [showFlare, setShowFlare] = useState<boolean>(false);
  const [clickFeedback, setClickFeedback] = useState<{x: number, y: number, show: boolean}>({x: 0, y: 0, show: false});
  const [pendingAutoAttack, setPendingAutoAttack] = useState<boolean>(false);
  const [highScores, setHighScores] = useState<HighScore[]>([]);
  const [currentSubSequence, setCurrentSubSequence] = useState<number>(0);
  const [subSequencePosition, setSubSequencePosition] = useState<number>(0);

  const { championsAssets, fetchAndSetChampionAssets, gameWideAssets } = useContext(GameAssetsContext);

  // Load high scores from localStorage
  useEffect(() => {
    const savedHighScores = localStorage.getItem('comboInputPractice_highScores');
    if (savedHighScores) {
      try {
        setHighScores(JSON.parse(savedHighScores));
      } catch (error) {
        console.error('Error loading high scores:', error);
      }
    }
  }, []);

  // Debug: Monitor showCompletion state changes
  useEffect(() => {
    console.log('🔄 showCompletion state changed:', showCompletion);
  }, [showCompletion]);

  // Save high score
  const saveHighScore = (newScore: HighScore) => {
    const updatedScores = [...highScores, newScore]
      .sort((a, b) => {
        // Sort by accuracy first (higher better), then by time (lower better)
        if (Math.abs(a.accuracy - b.accuracy) > 0.1) {
          return b.accuracy - a.accuracy;
        }
        return a.time - b.time;
      })
      .slice(0, 5); // Keep top 5 scores
    
    setHighScores(updatedScores);
    localStorage.setItem('comboInputPractice_highScores', JSON.stringify(updatedScores));
  };

  // Get high score for current combo
  const getCurrentComboHighScore = (): HighScore | null => {
    if (!selectedCombo) return null;
    return highScores
      .filter(score => score.comboName === selectedCombo.name)
      .sort((a, b) => {
        if (Math.abs(a.accuracy - b.accuracy) > 0.1) {
          return b.accuracy - a.accuracy;
        }
        return a.time - b.time;
      })[0] || null;
  };

  // Get current expected key based on sub-sequence logic
  const getCurrentExpectedKey = (): string | null => {
    const displaySeq = sequenceState.displaySequence;
    
    console.log('🔍 getCurrentExpectedKey called:', {
      currentSubSequence,
      subSequencePosition,
      displaySeqLength: displaySeq.length,
      firstFewKeys: displaySeq.slice(0, 5).map(k => ({
        baseKey: k.baseKey,
        subSequenceId: k.subSequenceId,
        subSequencePosition: k.subSequencePosition
      }))
    });
    
    // Find the key that matches our current sub-sequence and position
    for (const key of displaySeq) {
      if (key.subSequenceId === currentSubSequence && 
          key.subSequencePosition === subSequencePosition) {
        console.log('🎯 Found expected key:', key.baseKey);
        return key.baseKey;
      }
    }
    
    console.log('🏁 No expected key found - sequence should be complete');
    return null; // Sequence complete
  };

  // Check if current sub-sequence is complete
  const isCurrentSubSequenceComplete = (): boolean => {
    const displaySeq = sequenceState.displaySequence;
    
    // Find all keys in current sub-sequence
    const subSeqKeys = displaySeq.filter(key => key.subSequenceId === currentSubSequence);
    if (subSeqKeys.length === 0) return true;
    
    // Check if we're at the last position in this sub-sequence
    const maxPosition = Math.max(...subSeqKeys.map(key => key.subSequencePosition || 0));
    return subSequencePosition > maxPosition;
  };

  // Advance to next position in sub-sequence or next sub-sequence
  const advanceSubSequence = () => {
    const displaySeq = sequenceState.displaySequence;
    const subSeqKeys = displaySeq.filter(key => key.subSequenceId === currentSubSequence);
    
    if (subSeqKeys.length === 0) return; // No more sub-sequences
    
    const maxPosition = Math.max(...subSeqKeys.map(key => key.subSequencePosition || 0));
    
    if (subSequencePosition < maxPosition) {
      // Advance within current sub-sequence
      setSubSequencePosition(prev => prev + 1);
    } else {
      // Move to next sub-sequence
      setCurrentSubSequence(prev => prev + 1);
      setSubSequencePosition(0);
    }
  };

  // Load combo data from JSON file
  useEffect(() => {
    const loadCombos = async () => {
      try {
        const response = await fetch('/data/comboSequences.json');
        if (!response.ok) {
          throw new Error('Failed to load combo data');
        }
        const data: ComboDatabase = await response.json();
        COMBO_DATABASE = data;
        setCombos(data.combos);
        
        // Set default selected combo (first one)
        if (data.combos.length > 0) {
          setSelectedCombo(data.combos[0]);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error loading combo data:', error);
        setLoading(false);
      }
    };

    loadCombos();
  }, []);

  // Parse input sequence into individual keys (simple format)
  // Strips out fast-chain `>` separators so they don't become key presses
  const parseInputSequence = (inputSequence: string): string[] => {
    return inputSequence
      .replace(/>/g, '') // Remove fast-chain separators
      .toLowerCase()
      .split('')
      .map(char => char.toUpperCase())
      .filter(char => char.trim().length > 0);
  };

  // Convert detailed key to parsed key for rendering
  const convertDetailedKey = (detailedKey: DetailedKey, index: number): ParsedKey => {
    const baseKey = detailedKey.key.toUpperCase();
    
    // Determine key type
    let keyType: ParsedKey['keyType'] = 'spell';
    if (['Q', 'W', 'E', 'R'].includes(baseKey)) {
      keyType = 'spell';
    } else if (['D', 'F'].includes(baseKey)) {
      keyType = 'summoner';
    } else if (baseKey === 'A') {
      keyType = 'auto';
    } else if (baseKey === '4') {
      keyType = 'item';
    }
    
    return {
      baseKey,
      imageState: detailedKey.imageState,
      displayKey: baseKey,
      keyType,
      target: detailedKey.target,
      description: detailedKey.description
    };
  };

  // Parse individual key with enhanced tag system (legacy support)
  const parseKey = (keyString: string): ParsedKey => {
    const trimmed = keyString.trim();
    
    // Parse format: KEY[imageState|target:value|meta:value]
    const keyMatch = trimmed.match(/^([A-Z0-9]+)(?:\[([^\]]+)\])?$/);
    if (!keyMatch) {
      throw new Error(`Invalid key format: ${trimmed}`);
    }
    
    const baseKey = keyMatch[1].toUpperCase();
    const tagsString = keyMatch[2];
    
    let imageState: string | undefined;
    let target: string | undefined;
    let metaInfo: string | undefined;
    
    // Parse tags if they exist
    if (tagsString) {
      const tags = tagsString.split('|');
      for (const tag of tags) {
        if (tag.includes(':')) {
          const [tagType, tagValue] = tag.split(':', 2);
          switch (tagType) {
            case 'target':
              target = tagValue;
              break;
            case 'meta':
              metaInfo = tagValue;
              break;
            default:
              // Unknown tag type, treat as imageState for backwards compatibility
              imageState = tag;
              break;
          }
        } else {
          // No colon, treat as imageState
          imageState = tag;
        }
      }
    }
    
    // Determine key type
    let keyType: ParsedKey['keyType'] = 'spell';
    if (['Q', 'W', 'E', 'R'].includes(baseKey)) {
      keyType = 'spell';
    } else if (['D', 'F'].includes(baseKey)) {
      keyType = 'summoner';
    } else if (baseKey === 'A') {
      keyType = 'auto';
    } else if (baseKey === '4') {
      keyType = 'item';
    }
    
    return {
      baseKey,
      imageState,
      displayKey: baseKey,
      keyType,
      target
    };
  };

  // Parse sequence into individual parsed keys
  // Handles fast-chain notation (A>R → two separate ParsedKeys)
  const parseSequence = (sequence: string): ParsedKey[] => {
    return sequence
      .split(/[,\s]+/)
      .filter(token => token.trim().length > 0)
      .flatMap(token => {
        const trimmed = token.trim();
        // Split fast-chain tokens (e.g. "A>R" → ["A", "R"])
        if (trimmed.includes('>')) {
          return trimmed.split('>').filter(p => p.length > 0).map(parseKey);
        }
        return [parseKey(trimmed)];
      });
  };

  // Get current sequence being practiced (for display)
  const getCurrentSequence = (): ParsedKey[] => {
    let sequence: ParsedKey[] = [];
    
    if (selectedCombo) {
      sequence = selectedCombo.detailedSequence.map((detailedKey, index) => 
        convertDetailedKey(detailedKey, index)
      );
    } else if (customSequence) {
      sequence = parseSequence(customSequence);
    }
    
    // If kiting is enabled, insert right-click after every keyboard input
    if (kitingSettings.enabled && sequence.length > 0) {
      const kitedSequence: ParsedKey[] = [];
      sequence.forEach((key, index) => {
        kitedSequence.push(key);
        
        // Add left-click after auto attacks if attack-move click is enabled
        if (key.keyType === 'auto' && kitingSettings.attackMoveClick) {
          const lcKey = {
            baseKey: 'LC',
            displayKey: 'LC',
            keyType: 'leftclick' as const,
            description: 'Left Click to confirm auto attack'
          };
          kitedSequence.push(lcKey);
        }
        
        // Add right-click after every key except the last one
        if (index < sequence.length - 1) {
          const rcKey = {
            baseKey: 'RC',
            displayKey: 'RC',
            keyType: 'rightclick' as const,
            description: 'Right Click to kite'
          };
          kitedSequence.push(rcKey);
        }
      });
      
      // Filter out hidden button types for display
      let filteredSequence = kitedSequence;
      if (kitingSettings.hideRCButtons) {
        filteredSequence = filteredSequence.filter(key => key.keyType !== 'rightclick');
      }
      if (kitingSettings.hideLCButtons) {
        filteredSequence = filteredSequence.filter(key => key.keyType !== 'leftclick');
      }
      
      return filteredSequence;
    }
    
    return sequence;
  };

  // Get input sequence for keyboard checking
  const getCurrentInputSequence = (): string[] => {
    let sequence: string[] = [];
    
    if (selectedCombo) {
      sequence = parseInputSequence(selectedCombo.inputSequence);
    } else if (customSequence) {
      sequence = parseInputSequence(customSequence);
    }
    
    // When kiting is enabled, right-clicks are always optional
    // Only add required left-clicks for attack-move
    if (kitingSettings.enabled && sequence.length > 0) {
      const kitedSequence: string[] = [];
      sequence.forEach((key) => {
        kitedSequence.push(key);
        
        // Add left-click after auto attacks if attack-move click is enabled
        // This is the only additional required input when kiting
        if (key === 'A' && kitingSettings.attackMoveClick) {
          kitedSequence.push('LC');
        }
      });
      return kitedSequence;
    }
    
    return sequence;
  };

  // Split sequence into chunks of 6 for display
  const getSequenceChunks = (sequence: ParsedKey[], chunkSize: number = 6): ParsedKey[][] => {
    const chunks: ParsedKey[][] = [];
    for (let i = 0; i < sequence.length; i += chunkSize) {
      chunks.push(sequence.slice(i, i + chunkSize));
    }
    return chunks;
  };

  // Get current chunk index based on current key
  const getCurrentChunkIndex = (keyIndex: number, chunkSize: number = 6): number => {
    return Math.floor(keyIndex / chunkSize);
  };

  // Get the actual current key index for display purposes (excluding optional inputs)
  const getDisplayCurrentKeyIndex = (): number => {
    // Count only valid inputs (correct or incorrect but not optional)
    const validInputCount = userInputKeys.filter(input => !input.isOptional).length;
    return Math.min(validInputCount, getCurrentInputSequence().length - 1);
  };

  // Organize completion display with intelligent matching and RC positioning
  const getCompletionChunks = (): AlignedInput[][] => {
    const baseSequence = getCurrentSequence();
    const displaySequence = sequenceState.displaySequence;
    
    // Create intelligent alignment between expected and user inputs with smart RC positioning
    const alignedInputs: AlignedInput[] = [];
    
    let expectedIndex = 0;
    let userInputIndex = 0;
    
    // Process each expected key and align with user inputs
    while (expectedIndex < displaySequence.length) {
      const expectedKey = displaySequence[expectedIndex];
      const isRCKey = expectedKey.keyType === 'rightclick';
      
      if (isRCKey) {
        // RC keys get attached to the previous main key, skip processing here
        expectedIndex++;
        continue;
      }
      
      // Check if this is part of a multi-key sub-sequence (like A+LC)
      const subSequenceKeys = displaySequence.filter(k => 
        k.subSequenceId === expectedKey.subSequenceId && 
        k.keyType !== 'rightclick'
      );
      const isMultiKeySubSequence = subSequenceKeys.length > 1;
      
      const alignedInput = {
        expected: expectedKey,
        subSequenceKeys: isMultiKeySubSequence ? subSequenceKeys : undefined,
        userInputs: [] as Array<{key: string, isCorrect: boolean, isOptional?: boolean}>,
        followingRCs: [] as Array<{key: string, isCorrect: boolean, isOptional?: boolean}>,
        isMainKey: true,
        isSubSequenceGroup: isMultiKeySubSequence
      };
      
      // Handle input collection differently for sub-sequences vs single keys
      if (isMultiKeySubSequence && alignedInput.subSequenceKeys) {
        // For A+LC sub-sequences, collect inputs for each position
        const subSequenceInputs: Array<{key: string, isCorrect: boolean, isOptional?: boolean, position?: number}> = [];
        const followingRCs: Array<{key: string, isCorrect: boolean, isOptional?: boolean}> = [];
        
        // Track completion of each sub-sequence position
        let subSequenceCompleted = false;
        let currentSubPosition = 0; // 0 = A position, 1 = LC position
        
        while (userInputIndex < userInputKeys.length && !subSequenceCompleted) {
          const userInput = userInputKeys[userInputIndex];
          
          if (userInput.isOptional) {
            // RC input - determine if it's cancelling or following
            if (currentSubPosition > 0) {
              // RC during sub-sequence = cancellation (goes under LC position)
              subSequenceInputs.push({...userInput, isCorrect: false, position: 1}); // Mark as incorrect since it cancels
              currentSubPosition = 0; // Reset to A position
      } else {
              // RC at start or after completion = following RC
              followingRCs.push(userInput);
            }
            userInputIndex++;
            continue;
          }
          
          // Non-optional input
          if (currentSubPosition === 0) {
            // Expecting A
            if (userInput.key === 'A' && userInput.isCorrect) {
              subSequenceInputs.push({...userInput, position: 0});
              currentSubPosition = 1; // Move to LC position
            } else {
              // Wrong key at A position
              subSequenceInputs.push({...userInput, position: 0});
              // Don't advance position, stay at A
            }
          } else if (currentSubPosition === 1) {
            // Expecting LC
            if (userInput.key === 'LC' && userInput.isCorrect) {
              subSequenceInputs.push({...userInput, position: 1});
              subSequenceCompleted = true;
            } else {
              // Wrong key at LC position = cancellation (goes under LC position)
              subSequenceInputs.push({...userInput, isCorrect: false, position: 1});
              currentSubPosition = 0; // Reset to A position
            }
          }
          
          userInputIndex++;
        }
        
        alignedInput.userInputs = subSequenceInputs;
        alignedInput.followingRCs = followingRCs;
      } else {
        // Single key logic (unchanged)
        let foundCorrectMatch = false;
        const inputsForThisKey: Array<{key: string, isCorrect: boolean, isOptional?: boolean}> = [];
        const rcsAfterThisKey: Array<{key: string, isCorrect: boolean, isOptional?: boolean}> = [];
        
        while (userInputIndex < userInputKeys.length && !foundCorrectMatch) {
          const userInput = userInputKeys[userInputIndex];
          
          if (userInput.isOptional) {
            // Optional inputs (RC) - collect separately for positioning after the key
            rcsAfterThisKey.push(userInput);
            userInputIndex++;
            continue;
          }
          
          // Non-optional input
          inputsForThisKey.push(userInput);
          userInputIndex++;
          
          // Check if this input correctly matches the expected key
          if (userInput.isCorrect) {
            foundCorrectMatch = true;
            break;
          }
          // If incorrect, continue collecting inputs until we find the correct one
        }
        
        alignedInput.userInputs = inputsForThisKey;
        alignedInput.followingRCs = rcsAfterThisKey;
      }
      alignedInputs.push(alignedInput);
      
      // If this is a multi-key sub-sequence, skip the remaining keys in the sub-sequence
      if (isMultiKeySubSequence) {
        // Skip all keys in this sub-sequence
        while (expectedIndex < displaySequence.length && 
               displaySequence[expectedIndex].subSequenceId === expectedKey.subSequenceId) {
          expectedIndex++;
        }
      } else {
        expectedIndex++;
      }
      
      // Skip any RC keys that immediately follow this key in the expected sequence
      while (expectedIndex < displaySequence.length && displaySequence[expectedIndex].keyType === 'rightclick') {
        expectedIndex++;
      }
    }
    
    // Add any remaining user inputs that came after sequence completion
    while (userInputIndex < userInputKeys.length) {
      const remainingInput = userInputKeys[userInputIndex];
      // Add these as extra entries with no expected key
      alignedInputs.push({
        expected: { baseKey: '?', displayKey: '?', keyType: 'spell' as const, description: 'Extra input' },
        userInputs: [remainingInput],
        followingRCs: [],
        isMainKey: true
      });
      userInputIndex++;
    }
    
    // Organize into chunks (6 main keys per row)
    const chunks: typeof alignedInputs[] = [];
    let currentChunk: typeof alignedInputs = [];
    
    for (const alignedInput of alignedInputs) {
      if (currentChunk.length >= 6) {
        chunks.push([...currentChunk]);
        currentChunk = [];
      }
      
      currentChunk.push(alignedInput);
    }
    
    if (currentChunk.length > 0) {
      chunks.push(currentChunk);
    }
    
    return chunks;
  };

  // Load champion assets when combo is selected
  useEffect(() => {
    if (selectedCombo && !championsAssets[selectedCombo.championName]) {
      try {
        fetchAndSetChampionAssets(selectedCombo.championName);
      } catch (error) {
        console.warn('Failed to fetch champion assets:', error);
      }
    }
  }, [selectedCombo, championsAssets, fetchAndSetChampionAssets]);

  // Reset state when combo changes
  useEffect(() => {
    if (selectedCombo) {
      setCurrentKeyIndex(0);
      setUserInputKeys([]);
      setErrors(0);
      setShowCompletion(false);
    }
  }, [selectedCombo]);

  // Reset practice when kiting settings change (only when enabling/disabling kiting)
  useEffect(() => {
    if (practiceMode) {
      resetPractice();
    }
  }, [kitingSettings.enabled]); // Only reset when enabled state changes, not other settings

  // Unified input processing function
  const processInput = useCallback((inputKey: string, isRightClick: boolean = false, isLeftClick: boolean = false) => {
    if (!practiceMode) return;

    const expectedKey = getCurrentExpectedKey();
    if (!expectedKey) {
      console.log('🎯 SEQUENCE COMPLETE!');
      return;
    }
    
    console.group(`🎮 INPUT PROCESSING: "${inputKey}"`);
    console.log('📊 Current State:', {
      inputKey,
      isRightClick,
      isLeftClick,
      expectedKey,
      currentSubSequence,
      subSequencePosition,
      pendingAutoAttack,
      userInputKeysCount: userInputKeys.length,
      kitingSettings: {
        enabled: kitingSettings.enabled,
        attackMoveClick: kitingSettings.attackMoveClick
      }
    });

    // Handle left-click confirmation for pending auto attacks
    if (isLeftClick && pendingAutoAttack && kitingSettings.attackMoveClick && expectedKey === 'LC') {
      console.log('✅ LEFT-CLICK: Confirming pending auto attack');
      // Confirm the pending auto attack
      setPendingAutoAttack(false);
      setUserInputKeys(prev => [...prev, { key: 'LC', isCorrect: true }]);
      
      // Check completion BEFORE advancing (we need to check if this was the last key)
      const displaySeq = sequenceState.displaySequence;
      const maxSubSequenceId = displaySeq.length > 0 ? Math.max(...displaySeq.map(k => k.subSequenceId || 0)) : -1;
      const isLastSubSequence = currentSubSequence === maxSubSequenceId;
      const isLastPositionInSubSequence = displaySeq.filter(k => k.subSequenceId === currentSubSequence).length === subSequencePosition + 1;
      const isSequenceComplete = isLastSubSequence && isLastPositionInSubSequence;
      
      console.log('🔍 LC PRE-ADVANCE COMPLETION CHECK:', {
        currentSubSequence,
        subSequencePosition,
        maxSubSequenceId,
        isLastSubSequence,
        isLastPositionInSubSequence,
        isSequenceComplete
      });
      
      // LC completes the A+LC sub-sequence - move to NEXT sub-sequence (not just advance position)
      console.log('🔄 LC ADVANCEMENT: Completing A+LC sub-sequence, moving to next sub-sequence');
      setCurrentSubSequence(prev => prev + 1);
      setSubSequencePosition(0);
      
      if (isSequenceComplete) {
        console.log('🎯 SEQUENCE COMPLETE!');
        const endTimestamp = Date.now();
        setEndTime(endTimestamp);
        setPracticeMode(false);
        setShowCompletion(true);
        
        // Save high score
        if (selectedCombo && startTime) {
          const totalTime = ((endTimestamp - startTime) / 1000);
          const totalErrors = userInputKeys.filter(key => !key.isCorrect && !key.isOptional).length;
          const totalKeys = getCurrentInputSequence().length;
          const accuracy = ((totalKeys - totalErrors) / totalKeys) * 100;
          const kmp = Math.round((totalKeys / totalTime) * 60);
          
          const newScore: HighScore = {
            comboName: selectedCombo.name,
            time: totalTime,
            accuracy: Math.max(0, accuracy),
            kpm: kmp,
            errors: totalErrors,
            date: new Date().toISOString()
          };
          
          saveHighScore(newScore);
        }
      }
      console.groupEnd();
      return;
    }

    // Cancel pending auto attack if any other input is made
    if (pendingAutoAttack && !isLeftClick) {
      console.log('⚠️ CANCELLED: Pending auto attack cancelled by other input');
      setPendingAutoAttack(false);
      
      // Reset sub-sequence position back to the start of the sub-sequence (A position)
      setSubSequencePosition(0);
      
      // Mark the previous auto attack as incorrect since it wasn't confirmed
      setUserInputKeys(prev => {
        const updated = [...prev];
        // Find the last auto attack and mark it as incorrect
        for (let i = updated.length - 1; i >= 0; i--) {
          if (updated[i].key === 'A') {
            updated[i].isCorrect = false;
            break;
          }
        }
        return updated;
      });
      
      // Increment errors for the cancelled auto attack
      setErrors(prev => prev + 1);
      
      // Don't advance sub-sequence - stay on the same expected key (the A that needs to be re-done)
    }

    console.log('🔍 COMPARISON:', {
      inputKey,
      expectedKey,
      matches: inputKey === expectedKey,
      isRightClick,
      isLeftClick,
      currentSubSequence,
      subSequencePosition
    });

    if (inputKey === expectedKey) {
      console.log('✅ CORRECT: Input matches expected key');
      // Handle auto attack with attack-move click
      if (inputKey === 'A' && kitingSettings.attackMoveClick) {
        console.log('🎯 AUTO ATTACK: Setting pending auto attack, waiting for left-click');
        // Set pending auto attack, advance to LC position within sub-sequence
        setPendingAutoAttack(true);
        setUserInputKeys(prev => [...prev, { key: inputKey, isCorrect: true }]);
        // Advance to the LC position within the same sub-sequence
        setSubSequencePosition(prev => prev + 1);
        
        console.groupEnd();
        return;
      }
      
      // Correct input (non-auto or auto without attack-move click)
      console.log('✅ ADVANCING: Correct input, advancing sub-sequence');
      setUserInputKeys(prev => [...prev, { key: inputKey, isCorrect: true }]);
      
      // Check completion BEFORE advancing (we need to check if this was the last key)
      const displaySeq = sequenceState.displaySequence;
      const maxSubSequenceId = displaySeq.length > 0 ? Math.max(...displaySeq.map(k => k.subSequenceId || 0)) : -1;
      const isLastSubSequence = currentSubSequence === maxSubSequenceId;
      const isLastPositionInSubSequence = displaySeq.filter(k => k.subSequenceId === currentSubSequence).length === subSequencePosition + 1;
      const isSequenceComplete = isLastSubSequence && isLastPositionInSubSequence;
      
      console.log('🔍 PRE-ADVANCE COMPLETION CHECK:', {
        currentSubSequence,
        subSequencePosition,
        maxSubSequenceId,
        isLastSubSequence,
        isLastPositionInSubSequence,
        isSequenceComplete
      });
      
      // Advance sub-sequence
      advanceSubSequence();
      
      if (isSequenceComplete) {
        console.log('🎯 SEQUENCE COMPLETE!');
        const endTimestamp = Date.now();
        setEndTime(endTimestamp);
        setPracticeMode(false);
        setShowCompletion(true);
        
        // Save high score
        if (selectedCombo && startTime) {
          const totalTime = ((endTimestamp - startTime) / 1000);
          const totalErrors = userInputKeys.filter(key => !key.isCorrect && !key.isOptional).length;
          const totalKeys = getCurrentInputSequence().length;
          const accuracy = ((totalKeys - totalErrors) / totalKeys) * 100;
          const kpm = Math.round((totalKeys / totalTime) * 60);
          
          const newScore: HighScore = {
            comboName: selectedCombo.name,
            time: totalTime,
            accuracy: Math.max(0, accuracy),
            kpm: kpm,
            errors: totalErrors,
            date: new Date().toISOString()
          };
          
          saveHighScore(newScore);
        }
      }
    } else if (isRightClick) {
      console.log('🔵 OPTIONAL: Right-click - always optional, not advancing sequence');
      // Right-clicks are always optional when kiting is enabled - don't advance sequence
      setUserInputKeys(prev => [...prev, { key: inputKey, isCorrect: false, isOptional: true }]);
      // Don't increment errors or advance sequence for optional inputs
    } else if (
      (!isRightClick && !isLeftClick && ['Q', 'W', 'E', 'R', 'A', 'D', 'F', '4'].includes(inputKey))
    ) {
      console.log('❌ INCORRECT: Wrong input detected, adding error');
      // Wrong input (but valid)
      setUserInputKeys(prev => [...prev, { key: inputKey, isCorrect: false }]);
      setErrors(prev => prev + 1);
    } else {
      console.log('🚫 IGNORED: Input not recognized or not valid');
    }
    
    console.log('📝 Updated State:', {
      newUserInputKeysCount: userInputKeys.length + 1,
      newCurrentSubSequence: currentSubSequence,
      newSubSequencePosition: subSequencePosition,
      nextExpectedKey: getCurrentExpectedKey()
    });
    console.groupEnd();
  }, [practiceMode, currentSubSequence, subSequencePosition, getCurrentExpectedKey, advanceSubSequence, kitingSettings.attackMoveClick, pendingAutoAttack, getCurrentInputSequence, selectedCombo, startTime, userInputKeys, saveHighScore]);

  // Handle keyboard input
  const handleKeyPress = useCallback((event: KeyboardEvent) => {
    if (!practiceMode) return;
    const key = event.key.toUpperCase();
    processInput(key, false);
  }, [practiceMode, processInput]);

  // Handle mouse input (left and right clicks) - now specifically for practice area
  const handlePracticeAreaClick = useCallback((event: React.MouseEvent) => {
    if (!practiceMode || !kitingSettings.enabled) return;
    
    const isRightClick = event.button === 2;
    const isLeftClick = event.button === 0;
    
    // Only handle left and right clicks
    if (!isRightClick && !isLeftClick) return;
    
    if (isRightClick) {
      event.preventDefault(); // Prevent context menu
    }
    
    // Get click position relative to the practice area
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    // Trigger localized click feedback
    setClickFeedback({x, y, show: true});
    setTimeout(() => setClickFeedback(prev => ({...prev, show: false})), 400);
    
    // Process click input through unified system
    if (isRightClick) {
      processInput('RC', true, false);
    } else if (isLeftClick) {
      processInput('LC', false, true);
    }
  }, [practiceMode, kitingSettings.enabled, processInput]);

  // Add keyboard event listener
  useEffect(() => {
    if (practiceMode) {
      window.addEventListener('keydown', handleKeyPress);
      
      return () => {
        window.removeEventListener('keydown', handleKeyPress);
      };
    }
  }, [practiceMode, handleKeyPress]);

  // Start practice mode
  const startPractice = () => {
    const currentInputSeq = getCurrentInputSequence();
    if (currentInputSeq.length === 0) return;
    
    setCurrentKeyIndex(0);
    setUserInputKeys([]);
    setErrors(0);
    setStartTime(Date.now());
    setEndTime(null);
    setShowCompletion(false);
    setPracticeMode(true);
    setPendingAutoAttack(false);
    setCurrentSubSequence(0);
    setSubSequencePosition(0);
  };

  // Reset practice
  const resetPractice = () => {
    setPracticeMode(false);
    setCurrentKeyIndex(0);
    setUserInputKeys([]);
    setErrors(0);
    setStartTime(null);
    setEndTime(null);
    setShowCompletion(false);
    setPendingAutoAttack(false);
    setCurrentSubSequence(0);
    setSubSequencePosition(0);
  };

  // Universal icon resolver with summoner spell support
  const getKeyIcon = (parsedKey: ParsedKey): string | null => {
    const { baseKey, imageState, keyType } = parsedKey;
    
    // Handle summoner spells
    if (keyType === 'summoner') {
      // Map the key to the actual summoner spell
      let spellName: string | null = null;
      if (baseKey === summonerSettings.smite) {
        spellName = 'smite';
      } else if (baseKey === summonerSettings.flash) {
        spellName = 'flash';
      }
      
      if (spellName && gameWideAssets?.summonerSpellImages) {
        return gameWideAssets.summonerSpellImages[spellName] || null;
      }
      return null;
    }
    
    if (!selectedCombo) return null;
    const championName = selectedCombo.championName;
    
    // Handle spell abilities (Q, W, E, R)
    if (keyType === 'spell') {
      // First try championImages for image states
      const champMap = championImages[championName];
      if (champMap) {
        let finalIconUrl: string | undefined;
        
        // Try image state first (e.g., "empowered", "active")
        if (imageState && imageState !== 'default') {
          // Try various state formats
          const stateVariants = [
            `${baseKey.toLowerCase()}_${imageState}`, // q_empowered
            `${baseKey.toLowerCase()}${imageState}`,  // qempowered
            imageState, // just the state name
          ];
          
          for (const variant of stateVariants) {
            if (champMap[variant]) {
              finalIconUrl = champMap[variant];
              break;
            }
          }
        }
        
        // Fallback to base ability key
        if (!finalIconUrl) {
          finalIconUrl = champMap[baseKey.toLowerCase()];
        }
        
        if (finalIconUrl) {
          return finalIconUrl;
        }
      }
      
      // Fallback to championsAssets
      const championAssets = championsAssets[championName];
      if (championAssets?.spellImageUrls) {
        return championAssets.spellImageUrls[baseKey] || null;
      }
    }
    
    // Handle auto attacks
    if (keyType === 'auto') {
      const championAssets = championsAssets[championName];
      return championAssets?.autoAttackImageUrl || null;
    }
    
    // Handle right-click (no specific image, will use placeholder)
    if (keyType === 'rightclick') {
      return null;
    }
    
    // Other key types don't have champion-specific images
    return null;
  };

  // Calculate overall stats
  const calculateStats = () => {
    if (!startTime || !endTime) return null;
    
    const totalTime = (endTime - startTime) / 1000;
    const currentInputSeq = getCurrentInputSequence();
    const totalKeys = currentInputSeq.length;
    const kpm = Math.round(totalKeys / (totalTime / 60));
    const actualErrors = userInputKeys.filter(key => !key.isCorrect && !key.isOptional).length;
    const accuracy = Math.round(((totalKeys - actualErrors) / totalKeys) * 100);
    
    return { kpm, accuracy, totalTime: Math.round(totalTime), totalKeys };
  };

  // Use centralized sequence logic
  // Debug logging reduced - check the visual debug component below for detailed info
  
  const sequenceState = useSequenceLogic(
    selectedCombo,
    customSequence,
    kitingSettings,
    userInputKeys,
    currentKeyIndex,
    parseSequence,
    parseInputSequence,
    convertDetailedKey,
    currentSubSequence,
    subSequencePosition
  );

  const stats = calculateStats();
  const currentSeq = sequenceState.displaySequence;
  const sequenceChunks = sequenceState.sequenceChunks;
  const currentChunkIndex = sequenceState.currentProgress.currentChunkIndex;

  return (
    <div className="combo-input-practice">
      <h2>Combo Input Practice</h2>
      
      {/* Summoner Spell Settings */}
      <div className="summoner-settings">
        <h3>Summoner Spell Bindings</h3>
        <div className="summoner-inputs">
          <div className="summoner-input">
            <label>Smite Key:</label>
            <input
              type="text"
              value={summonerSettings.smite}
              onChange={(e) => setSummonerSettings(prev => ({ ...prev, smite: e.target.value.toUpperCase() }))}
              maxLength={1}
              className="key-input"
            />
          </div>
          <div className="summoner-input">
            <label>Flash Key:</label>
            <input
              type="text"
              value={summonerSettings.flash}
              onChange={(e) => setSummonerSettings(prev => ({ ...prev, flash: e.target.value.toUpperCase() }))}
              maxLength={1}
              className="key-input"
            />
          </div>
        </div>
      </div>



      {/* Combo Selection */}
      <ComboSelector
        combos={combos}
        selectedCombo={selectedCombo}
        onComboSelect={(combo) => {
                  setSelectedCombo(combo);
                  setCustomSequence('');
                  resetPractice();
                }}
        customSequence={customSequence}
        onCustomSequenceChange={(sequence) => {
          setCustomSequence(sequence);
                setSelectedCombo(null);
                resetPractice();
              }}
        loading={loading}
      />

      {/* Kiting Settings */}
      <KitingSettings
        kitingSettings={kitingSettings}
        onKitingSettingsChange={setKitingSettings}
        show={currentSeq.length > 0}
      />

      {/* Practice Area - always shown when there's a sequence */}
      {currentSeq.length > 0 && (
        <PracticeArea
          selectedCombo={selectedCombo}
          currentSeq={currentSeq}
          currentKeyIndex={sequenceState.currentProgress.currentKeyIndex}
          kitingSettings={kitingSettings}
          clickFeedback={clickFeedback}
          sequenceChunks={sequenceChunks}
          currentChunkIndex={currentChunkIndex}
          progress={sequenceState.currentProgress}
          keyStates={sequenceState.keyStates}
          userInputKeys={userInputKeys}
          practiceMode={practiceMode}
          showCompletion={showCompletion}
          stats={stats}
          highScore={getCurrentComboHighScore()}
          pendingAutoAttack={pendingAutoAttack}
          onPracticeAreaClick={handlePracticeAreaClick}
          onRetryClick={resetPractice}
          onStartPractice={startPractice}
          renderKeyButton={renderKeyButton}
          getCompletionChunks={getCompletionChunks}
        />
      )}

      {/* Debug Component - shows sequence assessment in real-time */}
      {/* {currentSeq.length > 0 && (
        <SequenceDebugger
          displaySequence={sequenceState.displaySequence}
          inputSequence={sequenceState.inputSequence}
          userInputKeys={userInputKeys}
          currentKeyIndex={currentKeyIndex}
          progress={sequenceState.currentProgress}
          keyStates={sequenceState.keyStates}
          kitingSettings={{
            enabled: kitingSettings.enabled,
            hideRCButtons: kitingSettings.hideRCButtons,
            hideLCButtons: kitingSettings.hideLCButtons
          }}
        />
      )} */}






    </div>
  );

  // Helper function to render key buttons with enhanced info
  function renderKeyButton(parsedKey: ParsedKey, isCompleted: boolean, isCurrent: boolean, isUpcoming: boolean) {
    const iconUrl = getKeyIcon(parsedKey);
    
    // Determine the actual key to display based on summoner settings
    let displayKey = parsedKey.displayKey;
    if (parsedKey.keyType === 'summoner') {
      if (parsedKey.imageState === 'smite') {
        displayKey = summonerSettings.smite;
      } else if (parsedKey.imageState === 'flash') {
        displayKey = summonerSettings.flash;
      }
    }
    
    return (
      <div className={`ability-key-button ${parsedKey.keyType} ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''} ${isUpcoming ? 'upcoming' : ''}`}>
        <div className="key-image-container">
          {iconUrl ? (
            <img src={iconUrl} alt={displayKey} className="key-image" />
          ) : (
            <div className={`key-placeholder ${parsedKey.keyType}`}>
              {displayKey}
            </div>
          )}
          <div className="key-overlay">
            {displayKey}
          </div>
        </div>
   {/*      {parsedKey.target && (
          <div className="target-indicator">
            {parsedKey.target}
          </div>
        )}
        {parsedKey.imageState && parsedKey.keyType === 'spell' && (
          <div className="state-indicator">
            {parsedKey.imageState}
          </div>
        )} */}
      </div>
    );
  }
};

export default ComboInputPractice;